import { useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import ArticleHero from './components/ArticleHero';
import ReviewSection from './components/ReviewSection';
import RecommendSection from './components/RecommendSection';
import { featuredComics } from '@/mocks/comics';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const comicId = parseInt(id || '1', 10);
  const comic = featuredComics.find((c) => c.id === comicId) || featuredComics[0];

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩漫漫画网`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: '2026-01-01',
    dateModified: comic.updateDate,
    publisher: {
      '@type': 'Organization',
      name: '韩漫漫画网',
    },
  };

  const bcJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://ihua3.com/' },
      { '@type': 'ListItem', position: 2, name: '分类浏览', item: 'http://ihua3.com/category' },
      { '@type': 'ListItem', position: 3, name: comic.title },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(bcJsonLd)}</script>
      <SEOHead
        title={`${comic.title} - ${comic.category} | 韩漫漫画网`}
        description={`${comic.title}：${comic.description} 作者：${comic.author}，评分${comic.rating}分，共${comic.chapters}话，${comic.status}。韩漫漫画网提供高清免费在线阅读。`}
        keywords={`${comic.title},${comic.category},韩国漫画,韩漫漫画网`}
        canonicalUrl={`http://ihua3.com/article/${comicId}`}
        ogType="article"
      />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '分类浏览', path: '/category' },
              { label: comic.title },
            ]}
          />
        </div>
        <ArticleHero comicId={comicId} />
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-[1000px] mx-auto px-4 md:px-6">
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-6 whitespace-nowrap">
              作品<span className="text-primary-500">详情</span>
            </h1>
            <div className="prose prose-sm md:prose-base max-w-none">
              <p className="text-foreground-700 leading-relaxed">
                {comic.description}
              </p>
              <div className="flex flex-wrap gap-2 mt-6">
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium whitespace-nowrap">
                    #{tag}
                  </span>
                ))}
              </div>
              <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="p-4 rounded-lg bg-background-100 text-center">
                  <p className="text-2xl font-bold font-heading text-primary-500 whitespace-nowrap">{comic.rating}</p>
                  <p className="text-xs text-foreground-500 mt-1 whitespace-nowrap">评分</p>
                </div>
                <div className="p-4 rounded-lg bg-background-100 text-center">
                  <p className="text-2xl font-bold font-heading text-foreground-900 whitespace-nowrap">{comic.chapters}</p>
                  <p className="text-xs text-foreground-500 mt-1 whitespace-nowrap">总话数</p>
                </div>
                <div className="p-4 rounded-lg bg-background-100 text-center">
                  <p className="text-2xl font-bold font-heading text-foreground-900 whitespace-nowrap">{comic.views}</p>
                  <p className="text-xs text-foreground-500 mt-1 whitespace-nowrap">阅读量</p>
                </div>
                <div className="p-4 rounded-lg bg-background-100 text-center">
                  <p className="text-2xl font-bold font-heading text-primary-500 whitespace-nowrap">{comic.status}</p>
                  <p className="text-xs text-foreground-500 mt-1 whitespace-nowrap">状态</p>
                </div>
              </div>
            </div>
          </div>
        </section>
        <ReviewSection />
        <RecommendSection currentId={comicId} />
      </main>
      <Footer />
    </>
  );
}